import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { NODE_DATA_BY_TYPE, NODE_TYPES } from 'modules/workflows/constants';
import { getIsNodeEditorReadOnly, updateNodeData } from 'modules/workflows/ducks';
import { defaultErrorState } from 'modules/workflowNodeEditors/components/formElements/DurationForm';
import { HoldNodeData } from 'modules/workflows/types';

import { isEmpty } from 'lodash';
import {
  RuleBuilderProvider,
  useRuleBuilder,
} from 'modules/journey/components/RuleBuilder/hooks/useRuleBuilder';
import { Rules } from 'modules/journey/components/RuleBuilder/types';
import { EditorBaseProps } from '../../types';
import HoldDurationSection from './HoldDurationSection';
import {
  StyledBanner,
  StyledHeader,
  StyledSection,
  StyledTitle,
  StyledLink,
} from './HoldEditor.styled';
import {
  TriggerRule,
  transformRulesToTriggerRules,
  transformTriggerRulesToRuleBlockRules,
} from './HoldEditor.utils';
import { HoldRuleBuilder } from './HoldRuleBuilder';
import { ReadOnlyHoldRules } from './HoldRuleBuilder/ReadOnlyHoldRules';

export type HoldEditorProps = EditorBaseProps<HoldNodeData>;

export const HOLD_EDITOR_TEST_ID = 'hold-editor';

export const HoldEditorView = (props: HoldEditorProps) => {
  const { nodeId } = props;
  const { state } = useRuleBuilder();

  const dispatch = useDispatch();
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);
  // TODO: Implement clone warning functionality
  const isCloneWarningBannerShown = false;

  const [nodeData, setNodeData] = useState<HoldNodeData>(
    props.nodeData || NODE_DATA_BY_TYPE[NODE_TYPES.HOLD],
  );

  /** Error state for custom duration */
  const [errorState, setErrorState] = useState(defaultErrorState);
  const hasDurationError = Object.values(errorState).some((err) => !!err);

  const handleUpdate = () => {
    const triggerRule = !isEmpty(state.rules?.[0]?.childRules)
      ? transformRulesToTriggerRules(state.rules)
      : null;

    const updatedNodeData = { ...nodeData, triggerRule };
    dispatch(updateNodeData(updatedNodeData, nodeId));
  };

  return (
    <NodeEditor
      {...props}
      canUpdate={!isTileEditorReadOnly && !hasDurationError}
      dataTest={HOLD_EDITOR_TEST_ID}
      initialNodeData={props.nodeData}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
      onUpdate={handleUpdate}
      updatedNodeData={nodeData}
    >
      <StyledSection>
        <StyledTitle>Hold criteria</StyledTitle>
        <StyledHeader>Pause users at this tile until:</StyledHeader>
        {isTileEditorReadOnly ? <ReadOnlyHoldRules /> : <HoldRuleBuilder />}
      </StyledSection>
      <StyledSection>
        <HoldDurationSection
          onChange={(updatedData = {}) => setNodeData({ ...nodeData, ...updatedData })}
          value={nodeData}
          isReadOnly={isTileEditorReadOnly}
          errorState={errorState}
          setErrorState={setErrorState}
          hasError={hasDurationError}
        />
      </StyledSection>
      <StyledBanner>
        Want to delay users based on an existing user property? Try a{' '}
        <StyledLink
          href="https://support.iterable.com/hc/en-us/articles/14319818758036-Adding-Delays-to-a-Journey-#time-delay-tiles"
          target="_blank"
          rel="noreferrer noopener"
        >
          Time Delay tile
        </StyledLink>
        .
      </StyledBanner>
    </NodeEditor>
  );
};

export const HoldEditor = (props: HoldEditorProps) => {
  const triggerRule = props?.nodeData?.triggerRule as TriggerRule;

  // soon to support multiple rules! probably.
  const rules = useMemo(
    () =>
      !isEmpty(triggerRule) ? (transformTriggerRulesToRuleBlockRules([triggerRule]) as Rules) : [],
    [triggerRule],
  );
  return (
    <RuleBuilderProvider
      rules={rules}
      isClonedWithErrors={false} // deprecated approach, switching to server-based errors once fully complete
      isLoading={false}
      serverReportedCloningErrors={triggerRule?.cloningErrorsList}
    >
      <HoldEditorView {...props} />
    </RuleBuilderProvider>
  );
};
